import { useMutation } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export type DeepDiveOutcome =
  | { kind: 'text'; text: string }
  | { kind: 'paywall'; params: { idx: number; localDate: string; lang: string; tone: string }; stars: number };

type PollResult = { text: string } | { error: 'refunded' };

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_TRIES = 20;

export function useBriefTopicDeepDive(): {
  mutate: (idx: number) => void;
  isPending: boolean;
  isError: boolean;
  data: DeepDiveOutcome | undefined;
  reset: () => void;
  pollResult: (idx: number, localDate: string) => Promise<PollResult>;
} {
  const jwt = useAuth((s) => s.jwt);

  const mutation = useMutation<DeepDiveOutcome, { status: number }, number>({
    mutationFn: async (idx: number) => {
      if (!jwt) throw { status: 401 };
      const res = await apiFetch('/api/v1/me/daily-brief/topic', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ idx }),
      });
      if (res.status === 402) {
        const body = (await res.json()) as {
          error: string;
          sku: string;
          stars: number;
          params: { idx: number; localDate: string; lang: string; tone: string };
        };
        return { kind: 'paywall', params: body.params, stars: body.stars };
      }
      if (!res.ok) throw { status: res.status };
      const body = (await res.json()) as { text: string };
      return { kind: 'text', text: body.text };
    },
  });

  const pollResult = async (idx: number, localDate: string): Promise<PollResult> => {
    if (!jwt) return { error: 'refunded' };
    for (let i = 0; i < POLL_MAX_TRIES; i++) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      try {
        const res = await apiFetch(
          `/api/v1/me/daily-brief/topic/result?idx=${idx}&localDate=${encodeURIComponent(localDate)}`,
          jwt,
        );
        if (!res.ok) continue;
        const body = (await res.json()) as
          | { ready: false }
          | { ready: true; text: string }
          | { ready: true; error: 'refunded' };
        if (!body.ready) continue;
        if ('error' in body && body.error === 'refunded') return { error: 'refunded' };
        return { text: (body as { ready: true; text: string }).text };
      } catch {
        // network hiccup — retry
      }
    }
    return { error: 'refunded' };
  };

  return {
    mutate: (idx: number) => mutation.mutate(idx),
    isPending: mutation.isPending,
    isError: mutation.isError,
    data: mutation.data,
    reset: mutation.reset,
    pollResult,
  };
}
